import React, { useState } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import {
  IconCheck, IconX, IconChevronDown, IconChevronUp, IconUser,
} from '@tabler/icons-react';
import {
  usePendingReviews,
  useReviewSection,
  usePendingManagerRatings,
  useManagerRateSection,
} from '../../hooks/usePerformance';

// Main Component 
export default function KPIApprovals() {
  const orgId = localStorage.getItem('orgId') || '';

  const { data: pendingReviews, isLoading, refetch } = usePendingReviews(orgId);
  const reviewSection = useReviewSection();

  const [expandedItems, setExpandedItems] = useState([]);
  const [expandedSelfRated, setExpandedSelfRated] = useState([]);
  const [rejectComments, setRejectComments] = useState({});

  // Filters for pending manager ratings 
  const [filterCycleName, setFilterCycleName] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');

  const { data: employeeSections, isLoading: loadingEmployeeSections } = usePendingManagerRatings(
    orgId,
    filterCycleName,
    filterStartDate,
  );

  const managerRate = useManagerRateSection();

  const [managerRatings, setManagerRatings] = useState({});

  const handleManagerRatingChange = (sectionId, itemId, field, value) => {
    setManagerRatings(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [itemId]: {
          ...prev[sectionId]?.[itemId],
          [field]: value,
        },
      },
    }));
  };

  const handleManagerRate = async (section) => {
    const ratings = section.items
      .filter(item => item.id)
      .map(item => ({
        item_id: item.id,
        manager_rating: managerRatings[section.id]?.[item.id]?.manager_rating !== undefined
          ? parseInt(managerRatings[section.id][item.id].manager_rating, 10)
          : null,
        manager_comment: managerRatings[section.id]?.[item.id]?.manager_comment || '',
      }));
    try {
      await managerRate.mutateAsync({ sectionId: section.id, data: { ratings } });
    } catch {
    }
  };

  const toggleExpand = (id) =>
    setExpandedItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );

  const handleAcceptSection = async (review) => {
    try {
      await reviewSection.mutateAsync({
        sectionId: review.id,
        orgId,
        data: {
          action: 'accept',
          org: review.org,
          employee: review.employee,
          cycle_name: review.cycle_name,
          cycle_start_date: review.cycle_start_date,
          cycle_end_date: review.cycle_end_date,
          section_name: review.section_name,
        },
      });
      refetch();
    } catch (error) {
    }
  };

  const handleRejectSection = async (review) => {
    const comment = rejectComments[review.id] || '';
    try {
      await reviewSection.mutateAsync({
        sectionId: review.id,
        orgId,
        data: {
          action: 'reject',
          comment,
          org: review.org,
          employee: review.employee,
          cycle_name: review.cycle_name,
          cycle_start_date: review.cycle_start_date,
          cycle_end_date: review.cycle_end_date,
          section_name: review.section_name,
        },
      });
      setRejectComments(prev => ({ ...prev, [review.id]: '' }));
      refetch();
    } catch (error) {
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-blue text-white';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (isLoading) {
    return <div className="p-20 text-center text-sm text-gray-500">Loading pending approvals...</div>;
  }

  return (
    <>
      <Breadcrumb title="KPI Approvals" path="Approvals" />

      {/* ── Pending Reviews ─────────────────────────────────────────────────── */}
      <div className="bg-bg-dark rounded-lg border border-border overflow-hidden shadow-sm">
        <div className="px-20 py-15 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-text-primary">Pending KPI Reviews</h3>
            <p className="text-xs text-text-secondary mt-2">Accept or reject KPI sections submitted by your team</p>
          </div>
          {pendingReviews && pendingReviews.length > 0 && (
            <span className="bg-primary text-white text-[11px] font-bold px-10 py-4 rounded">
              {pendingReviews.length} Pending
            </span>
          )}
        </div>

        <div className="grid grid-cols-12 px-20 py-10 text-[11px] font-bold text-text-secondary uppercase tracking-widest border-b border-border">
          <div className="col-span-5 px-10">Employee / Section</div>
          <div className="col-span-4 px-10">Cycle</div>
          <div className="col-span-3 px-10">Status</div>
        </div>

        <div className="divide-y divide-border">
          {pendingReviews && pendingReviews.length > 0 ? (
            pendingReviews.map((review, idx) => {
              const isExpanded = expandedItems.includes(review.id);
              const rejectComment = rejectComments[review.id] || '';

              return (
                <div key={idx}>
                  <div className="grid grid-cols-12 px-20 py-15 items-center hover:bg-bg-light transition-colors">
                    <div
                      className="col-span-5 flex items-center gap-10 cursor-pointer"
                      onClick={() => toggleExpand(review.id)}
                    >
                      {isExpanded
                        ? <IconChevronUp size={14} className="text-text-secondary shrink-0" />
                        : <IconChevronDown size={14} className="text-text-secondary shrink-0" />
                      }
                      <div className="px-5">
                        <p className="text-[13px] font-bold text-text-primary">{review.employee_name || 'Employee'}</p>
                        <p className="text-xs text-text-secondary mt-2">{review.section_name}</p>
                      </div>
                    </div>

                    <div className="col-span-4 px-10">
                      <p className="text-[12px] text-text-secondary">{review.cycle_name || '—'}</p>
                    </div>

                    <div className="col-span-3 px-10">
                      <span className={`text-[11px] font-bold px-10 py-4 rounded ${getStatusColor(review.status)}`}>
                        {review.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-30 pb-25 bg-bg-light-100 border-t border-border">
                      {review.items && review.items.length > 0 && (
                        <div className="mt-20">
                          <div className="grid grid-cols-12 gap-15 mb-8 px-10">
                            <div className="col-span-3"><label className="text-[10px] font-bold text-text-secondary uppercase">KPI Title</label></div>
                            <div className="col-span-2 text-center"><label className="text-[10px] font-bold text-text-secondary uppercase">Weight %</label></div>
                            <div className="col-span-3"><label className="text-[10px] font-bold text-text-secondary uppercase">Description</label></div>
                            <div className="col-span-4"><label className="text-[10px] font-bold text-text-secondary uppercase">Means Of Verification</label></div>
                          </div>

                          {review.items.map((item) => (
                            <div key={item.id} className="mb-10">
                              <div className="border border-border rounded bg-bg-dark">
                                <div className="grid grid-cols-12 gap-15 px-10 py-10">
                                  <div className="col-span-3">
                                    <textarea readOnly rows={3} className="w-full text-[12px] border border-border rounded p-8 resize-none bg-bg-light text-text-primary outline-none" value={item.title || ''} />
                                  </div>
                                  <div className="col-span-2">
                                    <input readOnly type="number" className="w-full text-[12px] border border-border rounded p-8 text-center bg-bg-light text-text-primary outline-none" value={item.weight || ''} />
                                  </div>
                                  <div className="col-span-3">
                                    <textarea readOnly rows={3} className="w-full text-[12px] border border-border rounded p-8 resize-none bg-bg-light text-text-primary outline-none" value={item.description || ''} />
                                  </div>
                                  <div className="col-span-4">
                                    <textarea readOnly rows={3} className="w-full text-[12px] border border-border rounded p-8 resize-none bg-bg-light text-text-primary outline-none" value={item.means_of_verification || ''} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {review.status === 'submitted' && (
                        <div className="mt-20 pt-15 border-t border-border">
                          <div className="mb-12">
                            <label className="text-[10px] font-bold text-text-secondary uppercase block mb-5">
                              Rejection Reason <span className="font-normal normal-case">(required for reject)</span>
                            </label>
                            <textarea
                              rows={2}
                              value={rejectComment}
                              onChange={(e) => setRejectComments(prev => ({ ...prev, [review.id]: e.target.value }))}
                              placeholder="Reason for rejecting this section..."
                              className="w-full text-[12px] border border-border rounded p-8 resize-none outline-none focus:border-danger bg-bg-dark"
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <p className="text-[11px] text-text-secondary italic">Review this section and choose an action</p>
                            <div className="flex gap-8">
                              <button
                                onClick={() => handleRejectSection(review)}
                                disabled={reviewSection.isPending || !rejectComment.trim()}
                                className="btn btn-outline-danger btn-sm flex items-center gap-5 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <IconX size={12} />
                                Reject
                              </button>
                              <button
                                onClick={() => handleAcceptSection(review)}
                                disabled={reviewSection.isPending}
                                className="btn btn-primary btn-sm flex items-center gap-5 disabled:opacity-50"
                              >
                                <IconCheck size={12} />
                                Accept
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="p-40 text-center text-text-secondary">
              <p className="text-sm font-medium">No pending KPI reviews at this time.</p>
              <p className="text-xs mt-5">Reviews will appear here when employees submit their KPI sections.</p>
            </div>
          )}
        </div>
      </div>
      <div className="h-25" />

      {/* ── Employee Self-Ratings — only shown when self-rated sections exist ── */}
      {!loadingEmployeeSections && employeeSections && employeeSections.length > 0 && (
        <div className="bg-bg-dark rounded-lg border border-border overflow-hidden shadow-sm">
          <div className="px-20 py-15 border-b border-border">
            <div className="flex items-center gap-10 mb-15">
              <IconUser size={18} className="text-primary" />
              <div>
                <h3 className="text-base font-bold text-text-primary">Employee Self-Ratings</h3>
                <p className="text-xs text-text-secondary mt-2">View self-assessment ratings submitted by an employee after section acceptance</p>
              </div>
            </div>

            {/* Filters — both optional */}
            <div className="flex flex-wrap gap-15">
              <div>
                <label className="text-[10px] font-bold text-text-secondary uppercase block mb-5">Performance Cycle</label>
                <select
                  value={filterCycleName}
                  onChange={(e) => setFilterCycleName(e.target.value)}
                  className="text-[12px] border border-border rounded px-10 py-8 bg-bg-dark outline-none focus:border-primary min-w-[220px]"
                >
                  <option value="">All cycles</option>
                  <option value="Annual Performance Review">Annual Performance Review</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-text-secondary uppercase block mb-5">Cycle Start Date</label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="text-[12px] border border-border rounded px-10 py-8 outline-none focus:border-primary bg-bg-dark"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 px-20 py-10 text-[11px] font-bold text-text-secondary uppercase tracking-widest border-b border-border">
            <div className="col-span-5 px-10">Employee / Section</div>
            <div className="col-span-4 px-10">Cycle</div>
            <div className="col-span-3 px-10">Status</div>
          </div>

          <div className="divide-y divide-border">
            {loadingEmployeeSections ? (
              <div className="p-30 text-center text-text-secondary">
                <p className="text-[12px]">Loading...</p>
              </div>
            ) : !employeeSections || employeeSections.length === 0 ? (
              <div className="p-40 text-center text-text-secondary">
                <p className="text-sm font-medium">No self-rated sections yet.</p>
                <p className="text-xs mt-5">Sections will appear here once employees submit their self-ratings.</p>
              </div>
            ) : (
              employeeSections.map((section) => {
                const isExpanded = expandedSelfRated.includes(section.id);
                return (
                  <div key={section.id}>
                    <div className="grid grid-cols-12 px-20 py-15 items-center hover:bg-bg-light transition-colors">
                      <div
                        className="col-span-5 flex items-center gap-10 cursor-pointer"
                        onClick={() => setExpandedSelfRated(prev =>
                          prev.includes(section.id) ? prev.filter(i => i !== section.id) : [...prev, section.id]
                        )}
                      >
                        {isExpanded
                          ? <IconChevronUp size={14} className="text-text-secondary shrink-0" />
                          : <IconChevronDown size={14} className="text-text-secondary shrink-0" />
                        }
                        <div className="px-5">
                          <p className="text-[13px] font-bold text-text-primary">{section.employee_name || 'Employee'}</p>
                          <p className="text-xs text-text-secondary mt-2">{section.section_name}</p>
                        </div>
                      </div>

                      <div className="col-span-4 px-10">
                        <p className="text-[12px] text-text-secondary">{section.cycle_name || '—'}</p>
                      </div>

                      <div className="col-span-3 px-10">
                        <span className={`text-[11px] font-bold px-10 py-4 rounded ${getStatusColor(section.status)}`}>
                          {section.status?.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    {isExpanded && section.items && section.items.length > 0 && (
                      <div className="px-30 pb-25 bg-bg-light-100 border-t border-border">
                        <div className="mt-20">
                          {/* Item column headers */}
                          <div className="grid grid-cols-12 gap-15 mb-8 px-10">
                            <div className="col-span-3"><label className="text-[10px] font-bold text-text-secondary uppercase">KPI Title</label></div>
                            <div className="col-span-2 text-center"><label className="text-[10px] font-bold text-text-secondary uppercase">Weight %</label></div>
                            <div className="col-span-3"><label className="text-[10px] font-bold text-text-secondary uppercase">Description</label></div>
                            <div className="col-span-4"><label className="text-[10px] font-bold text-text-secondary uppercase">Means Of Verification</label></div>
                          </div>

                          {section.items.map((item) => (
                            <div key={item.id} className="mb-15 border border-border rounded bg-bg-dark">
                              {/* Main KPI fields row */}
                              <div className="grid grid-cols-12 gap-15 px-10 py-10">
                                <div className="col-span-3">
                                  <textarea readOnly rows={4} className="w-full text-[12px] border border-border rounded p-10 resize-none bg-bg-light text-text-primary outline-none" value={item.title || ''} />
                                </div>
                                <div className="col-span-2">
                                  <input readOnly type="number" className="w-full text-[12px] border border-border rounded p-10 text-center bg-bg-light text-text-primary outline-none" value={item.weight || ''} />
                                </div>
                                <div className="col-span-3">
                                  <textarea readOnly rows={4} className="w-full text-[12px] border border-border rounded p-10 resize-none bg-bg-light text-text-primary outline-none" value={item.description || ''} />
                                </div>
                                <div className="col-span-4">
                                  <textarea readOnly rows={4} className="w-full text-[12px] border border-border rounded p-10 resize-none bg-bg-light text-text-primary outline-none" value={item.means_of_verification || ''} />
                                </div>
                              </div>

                              {/* Self Assessment row — only shown when employee has rated */}
                              {item.self_rating !== null && item.self_rating !== undefined && (
                                <div className="flex items-center gap-20 px-10 pb-10 border-t border-border pt-10 bg-primary/10">
                                  <div className="flex items-center gap-8">
                                    <label className="text-[10px] font-bold text-text-secondary uppercase whitespace-nowrap">Self Rating</label>
                                    <input
                                      readOnly
                                      type="number"
                                      className="text-[14px] border border-border rounded px-4 h-[45px] w-[65px] text-center bg-bg-dark text-text-primary outline-none"
                                      value={item.self_rating ?? ''}
                                    />
                                  </div>
                                  <div className="flex-1 flex items-center gap-8">
                                    <label className="text-[10px] font-bold text-text-secondary uppercase whitespace-nowrap">Self Comment</label>
                                    <textarea
                                      readOnly
                                      rows={2}
                                      className="flex-1 text-[12px] border border-border rounded p-8 resize-none bg-bg-dark text-text-primary outline-none"
                                      value={item.self_comment || ''}
                                      placeholder="No comment"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Manager Rating row — editable */}
                              <div className="flex items-center gap-20 px-10 pb-10 border-t border-border pt-10 bg-success/10">
                                <div className="flex items-center gap-8">
                                  <label className="text-[10px] font-bold text-text-secondary uppercase whitespace-nowrap">Manager Rating</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="3"
                                    step="1"
                                    className="text-[14px] border border-border rounded px-4 h-[45px] w-[65px] text-center bg-bg-dark text-text-primary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                                    value={managerRatings[section.id]?.[item.id]?.manager_rating ?? (item.manager_rating ?? '')}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '') { handleManagerRatingChange(section.id, item.id, 'manager_rating', ''); return; }
                                      const num = Number(val);
                                      if (num < 1 || num > 3) return;
                                      handleManagerRatingChange(section.id, item.id, 'manager_rating', val);
                                    }}
                                  />
                                </div>
                                <div className="flex-1 flex items-center gap-8">
                                  <label className="text-[10px] font-bold text-text-secondary uppercase whitespace-nowrap">Manager Comment</label>
                                  <textarea
                                    rows={2}
                                    className="flex-1 text-[12px] border border-border rounded p-8 resize-none bg-bg-dark text-text-primary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                                    value={managerRatings[section.id]?.[item.id]?.manager_comment ?? (item.manager_comment || '')}
                                    onChange={(e) => handleManagerRatingChange(section.id, item.id, 'manager_comment', e.target.value)}
                                    placeholder="Add your rating comment..."
                                  />
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Save Rating button */}
                          <div className="flex justify-end mt-15">
                            <button
                              onClick={() => handleManagerRate(section)}
                              disabled={managerRate.isPending}
                              className="btn btn-primary btn-sm flex items-center gap-5 disabled:opacity-50"
                            >
                              <IconCheck size={13} />
                              {managerRate.isPending ? 'Saving...' : 'Save Rating'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
}
